import { useState } from "react";
import { useSpring, animated } from "@react-spring/web";
import { Loader2, Search, Unlink } from "lucide-react";
import { useSelectGitRepo } from "@/hooks/git";
import type { GitRepo } from "@/services/git/types";

// ─── Ping beacon shown only on the clicked repo row ───────────────────────────

function SelectingBeacon() {
  const ring = useSpring({
    from: { opacity: 0.8, scale: 1 },
    to: { opacity: 0, scale: 2.2 },
    loop: true,
    config: { duration: 900 },
  });

  return (
    <div className="relative flex h-3 w-3 shrink-0 items-center justify-center">
      <animated.div
        style={ring}
        className="absolute h-3 w-3 rounded-full bg-emerald-400"
      />
      <div className="h-2 w-2 rounded-full bg-emerald-400" />
    </div>
  );
}

// ─── First-time repo selection modal ─────────────────────────────────────────

export function RepoSelectorView({
  repos,
  reposLoading,
  onDisconnect,
  disconnecting,
}: {
  repos: GitRepo[];
  reposLoading: boolean;
  onDisconnect: () => void;
  disconnecting: boolean;
}) {
  const [filter, setFilter] = useState("");
  const [pendingId, setPendingId] = useState<string | null>(null);
  const { mutate: selectRepo } = useSelectGitRepo();

  const filtered = repos.filter((r) =>
    r.full_name.toLowerCase().includes(filter.toLowerCase()),
  );

  function handleSelect(id: string) {
    setPendingId(id);
    selectRepo(id);
  }

  const backdropSpring = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: { tension: 220, friction: 30, clamp: true },
  });

  const modalSpring = useSpring({
    from: { opacity: 0, transform: "translateY(16px)" },
    to: { opacity: 1, transform: "translateY(0px)" },
    config: { tension: 260, friction: 28, clamp: true },
  });

  return (
    <animated.div
      style={backdropSpring}
      className="absolute inset-0 z-50 flex items-center justify-center bg-background/60 backdrop-blur-sm"
    >
      <animated.div
        style={modalSpring}
        className="flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-[0_24px_64px_rgba(0,0,0,0.5)]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pb-3 pt-5">
          <p className="text-sm font-medium text-muted-foreground">
            To get started, select a repository
          </p>
          <button
            type="button"
            onClick={onDisconnect}
            disabled={disconnecting}
            title="Disconnects Git only — GitHub stays connected under Tools"
            className="flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-destructive disabled:opacity-50"
          >
            {disconnecting
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Unlink className="h-3.5 w-3.5" />}
            {disconnecting ? "Disconnecting…" : "Disconnect Git"}
          </button>
        </div>

        {/* Search */}
        <div className="px-6 pb-3">
          <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <input
              autoFocus
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search repositories…"
              className="flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground/50"
            />
          </div>
        </div>

        {/* List */}
        <div className="max-h-72 overflow-y-auto border-t border-border">
          {reposLoading ? (
            <>
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="border-b border-border/50 px-5 py-3 last:border-0"
                  style={{ opacity: Math.max(0.2, 1 - i * 0.15) }}
                >
                  <div className="mb-1.5 h-2.5 w-3/4 animate-pulse rounded-full bg-muted/40" />
                  <div className="h-2 w-1/3 animate-pulse rounded-full bg-muted/25" />
                </div>
              ))}
            </>
          ) : filtered.length === 0 ? (
            <p className="py-10 text-center text-xs text-muted-foreground">
              {filter ? "No repositories match your search." : "No repositories found."}
            </p>
          ) : (
            filtered.map((repo) => {
              const isPending = repo.id === pendingId;
              return (
                <button
                  key={repo.id}
                  type="button"
                  disabled={!!pendingId}
                  onClick={() => handleSelect(repo.id)}
                  className={[
                    "flex w-full items-center gap-3 border-b border-border/50 px-5 py-3 text-left transition-colors last:border-0",
                    isPending
                      ? "bg-emerald-500/8"
                      : "hover:bg-muted/30 disabled:opacity-40",
                  ].join(" ")}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={[
                        "truncate font-mono text-xs font-medium",
                        isPending ? "text-emerald-400" : "",
                      ].join(" ")}>
                        {repo.full_name}
                      </span>
                      {repo.private && (
                        <span className="shrink-0 rounded bg-purple-500/10 px-1.5 py-0.5 text-[9px] font-semibold text-purple-400">
                          private
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 font-mono text-[10px] text-muted-foreground">{repo.default_branch}</p>
                  </div>
                  {isPending && <SelectingBeacon />}
                </button>
              );
            })
          )}
        </div>
      </animated.div>
    </animated.div>
  );
}
